package screens.detail

import common.EColors
import common.ECssClasses
import components.Spacer.Spacer
import core.AppContext
import core.enums.EImages
import core.extensions.toClassName
import core.extensions.toColor
import core.ionic.IonCol
import core.ionic.IonContent
import core.ionic.IonGrid
import core.ionic.IonPage
import core.ionic.IonRow
import core.localStorage.LocalStorageDAO
import core.swiper.Swiper
import core.swiper.SwiperHtmlElement
import core.swiper.SwiperSlide
import core.util.ColourHelper
import csstype.AlignContent
import csstype.AlignItems
import csstype.AlignSelf
import csstype.BackgroundColor
import csstype.BackgroundImage
import csstype.BackgroundPosition
import csstype.BackgroundSize
import csstype.Display
import csstype.FlexDirection
import csstype.FlexWrap
import csstype.JustifyContent
import csstype.LineStyle
import csstype.NamedColor
import csstype.Position
import csstype.TextAlign
import csstype.Transform
import csstype.integer
import csstype.minus
import csstype.number
import csstype.pct
import csstype.px
import emotion.react.css
import react.FC
import react.Props
import react.dom.html.ReactHTML
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.ref
import react.router.useNavigate
import react.useContext
import react.useRef
import react.useState

/**
 * Copyright (c) emgress GmbH. All rights reserved.
 *
 * Created on : 28.11.22
 * Author : mariorosshirt
 */

external interface ColourProductDetailProps : Props {

}

val ColourProductDetailScreen = FC<ColourProductDetailProps> { props ->

    val mContext by useContext(AppContext)
    val (mLocalStorage, mLocalStorageDispatch) = mContext.mLocalStorageReducer

    val mColourProductObject by useState(mLocalStorage.mDetailColourProductObject)

    val mIsColourLight = mColourProductObject?.let { ColourHelper.getContrastIsBlack(it.mColourCode) }

    val mNavigate = useNavigate()

    val mSwiperRef = useRef<SwiperHtmlElement>(null)

    var mLocalSliderIterator by useState(0)

    fun determineActiveSlide() {
        mLocalSliderIterator = mSwiperRef.current?.swiper?.realIndex ?: 0
    }

    IonPage {
        IonContent {
            IonGrid {
                css(
                    classNames = arrayOf(
                        ECssClasses.VERTICAL_GRID.toClassName(),
                        ECssClasses.NON_SCROLLABLE_GRID.toClassName()
                    )
                ) {}
                IonRow {
                    css(ECssClasses.FULL_WIDTH_ROW.toClassName()) {
                        flexGrow = number(1.0)
                    }
                    IonCol {
                        size = "12"
                        css(ECssClasses.FULL_WIDTH_COL.toClassName(), ECssClasses.FLEXBOX_COL.toClassName()) {}
                        div {
                            css {
                                position = Position.relative
                                width = 100.pct
                                flexGrow = number(1.0)
                                zIndex = integer(1)
                            }
                            Swiper {
                                css {
                                    height = 100.pct
                                }
                                loop = true
                                ref = mSwiperRef
                                onRealIndexChange = {
                                    determineActiveSlide()
                                }
                                onAfterInit = {
                                    mLocalSliderIterator = 0
                                }
                                SwiperSlide {
                                    css {
                                        backgroundColor = EColors.DECORATIVE_GRAY.toColor()
                                    }
                                    div {
                                        css {
                                            flexGrow = number(1.0)
                                        }
                                        img {
                                            src = "${mColourProductObject?.mImageUrl}?w=320&h=499"
                                        }
                                    }
                                }
                                SwiperSlide {
                                    css {
                                        backgroundColor = EColors.DECORATIVE_GRAY_NON_TRANSPARENT.toColor()
                                    }
                                    div {
                                        css {
                                            flexGrow = number(1.0)
                                            backgroundImage =
                                                "url('${mColourProductObject?.mInspirationImageUrl}')".unsafeCast<BackgroundImage>()
                                            backgroundPosition = BackgroundPosition.center
                                            backgroundSize = BackgroundSize.cover
                                            height = 100.pct
                                        }
                                    }
                                }
                                SwiperSlide {
                                    css {
                                        backgroundColor =
                                            mColourProductObject?.mColourCode.unsafeCast<BackgroundColor>()
                                        alignItems = AlignItems.flexStart
                                    }
                                    div {
                                        css {
                                            flexGrow = number(1.0)
                                            alignSelf = AlignSelf.flexStart
                                        }
                                        div {
                                            css(ECssClasses.BODY_FONT.toClassName(), ECssClasses.FLEXBOX_COL.toClassName()) {
                                                marginLeft = 20.px
                                                marginTop = 26.px
                                                textAlign = TextAlign.left
                                            }
                                            ReactHTML.b {
                                                css {
                                                    color =
                                                        if (mIsColourLight == true) NamedColor.black else NamedColor.white
                                                }
                                                +"${mColourProductObject?.mArtNr}"
                                            }
                                            ReactHTML.span {
                                                css {
                                                    color =
                                                        if (mIsColourLight == true) NamedColor.black else NamedColor.white
                                                }
                                                +"${mColourProductObject?.mName}"
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        button {
                            css(ECssClasses.CURSOR_HOVER.toClassName()) {
                                borderRadius = 50.pct
                                width = 72.px
                                height = 72.px
                                borderColor = NamedColor.white
                                opacity = number(0.7)
                                position = Position.absolute
                                bottom = 8.px
                                left = (-36).px
                                zIndex = integer(2)
                                transform = "translateZ(1px)".unsafeCast<Transform>()
                            }
                            img {
                                css {
                                    position = Position.absolute
                                    right = 6.px
                                    top = 23.px
                                }
                                src = EImages.SLIDER_ARROW_LEFT.mRelativePath
                            }
                            onClick = {
                                mLocalSliderIterator--
                                mSwiperRef.current?.swiper?.slidePrev()
                            }
                        }
                        button {
                            css(ECssClasses.CURSOR_HOVER.toClassName()) {
                                borderRadius = 50.pct
                                width = 72.px
                                height = 72.px
                                borderColor = NamedColor.white
                                opacity = number(0.7)
                                position = Position.absolute
                                bottom = 8.px
                                right = (-36).px
                                zIndex = integer(3)
                                transform = "translateZ(1px)".unsafeCast<Transform>()
                            }
                            img {
                                css {
                                    position = Position.absolute
                                    left = 6.px
                                    top = 23.px
                                }
                                src = EImages.SLIDER_ARROW_RIGHT.mRelativePath
                            }
                            onClick = {
                                mLocalSliderIterator++
                                mSwiperRef.current?.swiper?.slideNext()
                            }
                        }
                    }
                }
                IonRow {
                    css(ECssClasses.FULL_WIDTH_ROW.toClassName()) {}
                    IonCol {
                        size = "12"
                        css(ECssClasses.FULL_WIDTH_COL.toClassName()) { }
                        div {
                            css(ECssClasses.FLEXBOX_COL.toClassName()) {
                                flexWrap = FlexWrap.wrap
                                justifyContent = JustifyContent.center
                                alignContent = AlignContent.center
                                position = Position.relative
                                paddingTop = 96.px
                                paddingBottom = 36.px
                                paddingLeft = 16.px
                                paddingRight = 16.px
                            }
                            ReactHTML.button {
                                css(ECssClasses.ACTION_BUTTON.toClassName()) {
                                    val buttonSize = 72
                                    borderRadius = 50.pct
                                    width = buttonSize.px
                                    height = buttonSize.px
                                    borderWidth = 8.px
                                    borderColor = NamedColor.white
                                    borderStyle = LineStyle.solid
                                    left = 50.pct - (buttonSize / 2).px
                                    top = (-35).px
                                    alignItems = AlignItems.center
                                    position = Position.absolute
                                    display = Display.flex
                                    justifyContent = JustifyContent.spaceAround
                                    zIndex = integer(1)
                                    transform = "translateZ(1px)".unsafeCast<Transform>()
                                }
                                onClick = {
                                    mLocalStorageDispatch.invoke(
                                        LocalStorageDAO.ELocalStorageAction.DELETE_DETAIL_COLOUR_PRODUCT
                                    )
                                    mNavigate.invoke(-1)
                                }
                                //disabled = closeButtonDisabled
                                ReactHTML.img {
                                    css {
                                        height = 26.px
                                        width = 26.px
                                    }
                                    src = EImages.CLOSE_ICON_WHITE.mRelativePath
                                }
                            }

                            div {
                                css() {
                                    alignItems = AlignItems.end
                                    justifyContent = JustifyContent.end
                                    display = Display.flex
                                    flexDirection = FlexDirection.row
                                    width = 100.pct
                                    position = Position.absolute
                                    top = 14.px
                                    left = (-32).px
                                }
                                repeat(3) {
                                    div {
                                        css {
                                            borderRadius = 50.pct
                                            backgroundColor =
                                                if (it == mLocalSliderIterator) EColors.SECONDARY_BUTTON.toColor() else EColors.DECORATIVE_GRAY_NON_TRANSPARENT.toColor()
                                            width = 10.px
                                            height = 10.px
                                            marginLeft = 9.px
                                        }
                                    }
                                }
                            }
                            Spacer()
                            ReactHTML.span {
                                css(ECssClasses.BODY_FONT.toClassName()) {
                                    display = Display.flex
                                    justifyContent = JustifyContent.center
                                }
                                +"${mColourProductObject?.mColourCollection}"
                            }
                            ReactHTML.b {
                                css(ECssClasses.BODY_FONT.toClassName()) {
                                    textAlign = TextAlign.center
                                }
                                +"${mColourProductObject?.mArtNr} ${mColourProductObject?.mName}"
                            }
                            ReactHTML.span {
                                css(ECssClasses.BODY_FONT.toClassName()) {
                                    display = Display.flex
                                    justifyContent = JustifyContent.center
                                }
                                +"${mColourProductObject?.mAdditionalInfo1}"
                            }
                            ReactHTML.span {
                                css(ECssClasses.BODY_FONT.toClassName()) {
                                    display = Display.flex
                                    justifyContent = JustifyContent.center
                                }
                                +"${mColourProductObject?.mAdditionalInfo2}"
                            }
                        }
                    }
                }
            }
        }
    }
}